// src/pages/Products.jsx
import { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../context/AuthContext";
import ProductModal from "../components/ProductModal";
import axios from "axios";
import {
  Trash2,
  Edit,
  Search,
  Package,
  Plus,
  AlertTriangle,
  DollarSign,
  X,
  RefreshCw
} from "lucide-react";
import { showToast } from "../utils/toastUtils";

const BASE_URL = process.env.REACT_APP_API_URL || "";

const Products = () => {
  const { token, user } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  // Input Style
  const inputStyle = "w-full rounded-lg border-gray-200 bg-white px-4 py-2.5 text-gray-700 placeholder-gray-400 shadow-sm transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none hover:border-blue-300";

  useEffect(() => {
    if (token) axios.defaults.headers.common["x-auth-token"] = token;
    else delete axios.defaults.headers.common["x-auth-token"];
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const allQuery = user && user.role === "admin" ? "?all=true" : "";
        const url = `${BASE_URL}/api/products${allQuery}`;
        const res = await axios.get(url);

        const data = Array.isArray(res.data)
          ? res.data
          : (res.data && Array.isArray(res.data.products)
            ? res.data.products
            : (res.data && Array.isArray(res.data.data) ? res.data.data : []));
        if (!cancelled) setProducts(data);
      } catch (err) {
        console.error("[Products] fetch error:", err);
        setError(err.response?.data?.message || err.message || "Error fetching products");
        showToast("Error", err.response?.data?.message || "Error fetching products", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchProducts();
    return () => { cancelled = true; };
  }, [token, user]);

  const handleDeleteProduct = (product) => { setProductToDelete(product); setShowDeleteModal(true); };

  const confirmDeleteProduct = async () => {
    if (!productToDelete) return;
    try {
      await axios.delete(`${BASE_URL}/api/products/${productToDelete._id}`);
      setProducts(prev => prev.filter(p => p._id !== productToDelete._id));
      showToast("Success", "Product deleted successfully", "success");
    } catch (err) {
      console.error("Delete error:", err);
      showToast("Error", err.response?.data?.message || "Error deleting product", "error");
    } finally {
      setShowDeleteModal(false);
      setProductToDelete(null);
    }
  };

  const handleAddProduct = async (productData) => {
    try {
      const res = await axios.post(`${BASE_URL}/api/products`, productData);
      setProducts(prev => [res.data, ...prev]);
      showToast("Success", "Product added successfully", "success");
      return true;
    } catch (err) {
      console.error("Add error:", err);
      showToast("Error", err.response?.data?.message || "Error adding product", "error");
      return false;
    }
  };

  const handleEditProduct = async (productData) => {
    if (!editingProduct) return false;
    try {
      const res = await axios.put(`${BASE_URL}/api/products/${editingProduct._id}`, productData);
      setProducts(prev => prev.map(p => (p._id === res.data._id ? res.data : p)));
      showToast("Success", "Product updated successfully", "success");
      return true;
    } catch (err) {
      console.error("Update error:", err);
      showToast("Error", err.response?.data?.message || "Error updating product", "error");
      return false;
    }
  };

  const [syncProgress, setSyncProgress] = useState({ total: 0, processed: 0 });

  // Polling for sync status
  useEffect(() => {
    let interval;
    if (isSyncing) {
      interval = setInterval(async () => {
        try {
          const res = await axios.get(`${BASE_URL}/api/products/sync-status`);
          const { total, processed, status } = res.data;
          setSyncProgress({ total, processed });

          if (status === 'completed' || status === 'idle' || status === 'error') {
            if (status === 'completed' && processed > 0) {
              showToast("Sync Completed", `Processed ${processed}/${total} products.`, "success");
              setIsSyncing(false);
              setShowSyncModal(false);
            }
          }
        } catch (err) {
          console.error("Poll error", err);
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isSyncing]);

  const handleSyncClick = () => {
    setShowSyncModal(true);
  };

  const confirmSync = async () => {
    try {
      setIsSyncing(true);
      await axios.post(`${BASE_URL}/api/products/sync-woo`);
      showToast("Sync Started", "Sync started in background...", "info");
      // Modal stays open to show progress
    } catch (err) {
      console.error("Sync error:", err);
      showToast("Sync Failed", "Failed to start sync", "error");
      setIsSyncing(false);
    }
  };

  const openModal = (product = null) => { setEditingProduct(product); setIsModalOpen(true); };
  const closeModal = () => { setIsModalOpen(false); setEditingProduct(null); };

  // Filtered list using expanded search
  const filteredProducts = useMemo(() => {
    if (!searchTerm || searchTerm.trim() === "") return products;
    const s = String(searchTerm).trim().toLowerCase();
    return products.filter(p => {
      const itemNo = (p.itemNo || "").toString().toLowerCase();
      const name = (p.productName || "").toLowerCase();
      const code = (p.baseCode || "").toLowerCase();
      const type = (p.productType || "").toLowerCase();
      return itemNo.includes(s) || name.includes(s) || code.includes(s) || type.includes(s);
    });
  }, [products, searchTerm]);

  // KPI Calculations
  const totalProducts = products.length;
  const lowStockCount = products.filter(p => (p.stock || 0) < 10).length;
  // Calculate total value only if user is admin (optional, but safe)
  const totalValue = products.reduce((sum, p) => sum + ((p.stock || 0) * (p.mrp || 0)), 0);

  // Calculate percentage
  const progressPercent = syncProgress.total > 0 ? Math.round((syncProgress.processed / syncProgress.total) * 100) : 0;

  return (
    <div className="flex bg-gray-50 min-h-screen pb-12">
      <div className="flex-1 flex flex-col w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Package className="w-8 h-8 text-blue-600" /> My Products
            </h1>
            <p className="mt-2 text-sm text-gray-500">
              Manage your product inventory, pricing, and stock levels.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleSyncClick}
              disabled={isSyncing}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white text-gray-700 shadow-sm transition-all hover:bg-gray-50 ${isSyncing ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <RefreshCw className={`w-5 h-5 ${isSyncing ? "animate-spin" : ""}`} />
              {isSyncing ? `Syncing ${progressPercent}%` : "Sync with WooCommerce"}
            </button>

            <button
              onClick={() => openModal()}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 shadow-sm transition-all hover:shadow-md"
            >
              <Plus className="w-5 h-5" />
              Add New Product
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-start space-x-4 transition-all hover:shadow-md">
            <div className="p-3 rounded-lg bg-indigo-50 text-indigo-600">
              <Package className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Total Products</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">{totalProducts}</h3>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-start space-x-4 transition-all hover:shadow-md">
            <div className="p-3 rounded-lg bg-amber-50 text-amber-600">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Low Stock Items</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">{lowStockCount}</h3>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-start space-x-4 transition-all hover:shadow-md">
            <div className="p-3 rounded-lg bg-emerald-50 text-emerald-600">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Inventory Value</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">
                Rs. {totalValue.toLocaleString("en-LK", { maximumFractionDigits: 0 })}
              </h3>
            </div>
          </div>
        </div>

        {/* Search & Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
          <div className="relative group max-w-lg">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              placeholder="Search by name, item no, base code..."
              className={`${inputStyle} pl-10`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mb-4"></div>
              <p className="text-gray-500">Loading products...</p>
            </div>
          ) : error ? (
            <div className="p-6 text-center text-red-500 flex flex-col items-center">
              <AlertTriangle className="w-10 h-10 mb-2" />
              {error}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-4">
              <div className="bg-gray-50 rounded-full p-4 mb-4">
                <Search className="w-8 h-8 text-gray-300" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">No products found</h3>
              <p className="text-gray-500 mt-1">
                {products.length === 0 ? "Get started by adding a product." : "Try adjusting your search terms."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[250px]">Product Details</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[150px]">Category</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[120px]">Stock</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[120px]">MRP (Rs)</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[100px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProducts.map((product) => (
                    <tr key={product._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500">
                            <Package className="w-5 h-5" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{product.productName}</div>
                            <div className="text-xs text-gray-500">Item No: <span className="font-mono">{product.itemNo || "-"}</span></div>
                            {product.variation && (
                              <div className="text-xs text-gray-400 mt-0.5">{product.variation}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{product.productType || "-"}</div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 mt-1">
                          {product.baseCode || "-"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${(product.stock || 0) < 10
                          ? "bg-red-50 text-red-700 border-red-200"
                          : "bg-green-50 text-green-700 border-green-200"
                          }`}>
                          {product.stock} Units
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                        {typeof product.mrp === "number" ? product.mrp.toFixed(2) : "N/A"}
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openModal(product)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-transparent hover:border-blue-200"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteProduct(product)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-200"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <ProductModal onClose={closeModal} onSubmit={editingProduct ? handleEditProduct : handleAddProduct} initialData={editingProduct} />
      )}

      {/* Sync Confirmation Modal */}
      {showSyncModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-blue-600" /> Sync with WooCommerce
              </h2>
              {/* Only allow close if not syncing */}
              <button
                onClick={() => !isSyncing && setShowSyncModal(false)}
                className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
                disabled={isSyncing}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6">
              {!isSyncing ? (
                <>
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-semibold text-amber-800 mb-1 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> Important Warning
                    </h4>
                    <p className="text-sm text-amber-700 leading-relaxed">
                      You are about to overwrite <strong>WooCommerce stock levels</strong> with your current <strong>Ordly stock levels</strong>.
                    </p>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed mb-2">
                    • Products are matched by <strong>Item No (SKU)</strong>.
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    • This process runs in the background and may take a few minutes depending on your product count.
                  </p>
                </>
              ) : (
                <div className="py-2">
                  <div className="flex justify-between text-sm font-medium text-gray-700 mb-2">
                    <span>Syncing Products...</span>
                    <span>{Math.round((syncProgress.processed / (syncProgress.total || 1)) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                    <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style={{ width: `${Math.round((syncProgress.processed / (syncProgress.total || 1)) * 100)}%` }}></div>
                  </div>
                  <p className="text-xs text-gray-500 text-center">
                    Processed {syncProgress.processed} of {syncProgress.total} products
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              {!isSyncing && (
                <button
                  onClick={() => setShowSyncModal(false)}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              )}

              <button
                onClick={confirmSync}
                disabled={isSyncing}
                className="px-6 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition-all flex items-center gap-2 disabled:opacity-100" // Keep opacity 100 to show as status indicator
              >
                {isSyncing ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" /> Syncing...
                  </>
                ) : (
                  "Confirm Sync"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && productToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Delete Product?</h2>
              <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              Are you sure you want to delete <strong>{productToDelete.productName}</strong>? This action cannot be undone.
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteProduct}
                className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 shadow-sm hover:shadow-md transition-all"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default Products;